import { useCallback, useRef } from 'react';

import { v4 as uuid } from 'uuid';

declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext;
  }
}

type AudioBufferEntry = {
  id: string;
  buffer: AudioBuffer;
};

export type UsePlayAudioOptions = {
  audioBufferSource?: {
    onEnded?: (event: Event, entry: AudioBufferEntry) => void;
  };
  sampleRate?: number;
};

export type UsePlayAudioReturn = {
  playAudio: (base64Audio: string) => Promise<string | undefined>;
  stopAudio: () => number | null;
  resetCurrentAudioSegmentStats: () => void;
};

const DEFAULT_SAMPLE_RATE = 16_000;

const usePlayAudio = (options?: UsePlayAudioOptions): UsePlayAudioReturn => {
  const { audioBufferSource, sampleRate = DEFAULT_SAMPLE_RATE } = options || {};
  const audioContextRef = useRef<AudioContext | null>(null);
  const lastScheduledTimeRef = useRef<number>(0);
  const currentSourceRef = useRef<AudioBufferSourceNode | null>(null); // Reference to the current audio
  const audioSegmentSizeRef = useRef<number>(0); // Total bytes for the current audio segment
  const audioSegmentDurationRef = useRef<number>(0); // Total duration of the current audio segment

  const initAudioContext = async () => {
    if (audioContextRef.current) {
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }
      return;
    }
    const audioContext = new (window.AudioContext || window.webkitAudioContext)(
      {
        latencyHint: 'interactive',
        sampleRate,
      },
    );
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }
    audioContextRef.current = audioContext;
  };

  const playAudioBuffer = (entry: AudioBufferEntry) => {
    if (!audioContextRef.current) {
      return;
    }
    const audioContext = audioContextRef.current;
    const source = audioContext.createBufferSource();
    source.buffer = entry.buffer;

    // Connect the source to the audio context's destination (the speakers)
    source.connect(audioContext.destination);

    const lastScheduledTime = lastScheduledTimeRef.current;

    // Start playing the audio
    const startTime = Math.max(lastScheduledTime, audioContext.currentTime);
    source.start(startTime);
    lastScheduledTimeRef.current = startTime + entry.buffer.duration;

    currentSourceRef.current = source;

    if (audioBufferSource?.onEnded) {
      source.onended = (event: Event) => {
        audioBufferSource.onEnded?.(event, entry);
      };
    }
  };

  const playPCM = async (audioBytes: Uint8Array) => {
    if (!audioContextRef.current) {
      return;
    }
    const audioContext = audioContextRef.current;
    // 16khz F32LE
    const bytesPerSample = 32 / 8;
    const numSamples = audioBytes.byteLength / bytesPerSample;
    const audioBuffer = audioContext.createBuffer(1, numSamples, sampleRate);

    // Get the buffer data for the single channel
    const buffer = audioBuffer.getChannelData(0); // Get the data for the first channel

    // Create a DataView to read the PCM 32-bit samples
    const view = new DataView(audioBytes.buffer);

    // Fill the buffer with the PCM audio data
    for (let i = 0; i < numSamples; i++) {
      buffer[i] = view.getFloat32(i * bytesPerSample, true);
    }

    audioSegmentSizeRef.current += audioBytes.byteLength;
    audioSegmentDurationRef.current += audioBuffer.duration;

    const mediaId = uuid();
    playAudioBuffer({
      id: mediaId,
      buffer: audioBuffer,
    });
    return mediaId;
  };

  const resetCurrentAudioSegmentStats = useCallback(() => {
    // Reset segment tracking
    audioSegmentSizeRef.current = 0;
    audioSegmentDurationRef.current = 0;
  }, []);

  const playAudio: UsePlayAudioReturn['playAudio'] = async (
    base64Audio: string,
  ) => {
    await initAudioContext();
    const audioBytes = Uint8Array.from(atob(base64Audio), c => c.charCodeAt(0));
    return await playPCM(audioBytes);
  };

  const cleanup = useCallback(() => {
    audioSegmentSizeRef.current = 0;
    audioSegmentDurationRef.current = 0;
    audioContextRef.current?.close()?.catch(() => {});
    audioContextRef.current = null;
    lastScheduledTimeRef.current = 0;
  }, []);

  // Keep this memoised as it's used in UI interaction handlers
  const stopAudio: UsePlayAudioReturn['stopAudio'] = useCallback(() => {
    // Stop audio playback in event of barge-in and return the length of played bytes
    let playedBytes: number | null = null;

    if (currentSourceRef.current) {
      currentSourceRef.current.stop();
      currentSourceRef.current = null;
    }

    if (audioContextRef.current) {
      const audioContext = audioContextRef.current;
      const currentTime = audioContext.currentTime;

      if (lastScheduledTimeRef.current > currentTime) {
        const playedDuration =
          currentTime -
          (lastScheduledTimeRef.current - audioSegmentDurationRef.current);

        playedBytes = Math.ceil(
          (playedDuration / audioSegmentDurationRef.current) *
            audioSegmentSizeRef.current,
        );

        // Ensure playedBytes is aligned to 4-byte boundary
        playedBytes = Math.floor(playedBytes / 4) * 4;
      }
    }
    cleanup();
    return playedBytes;
  }, [cleanup]);

  return {
    playAudio,
    stopAudio,
    resetCurrentAudioSegmentStats,
  };
};

export default usePlayAudio;
